import {HttpParameterCodec, HttpParams} from '@angular/common/http';
import {Inject, Injectable, NgZone, PLATFORM_ID} from '@angular/core';
import {groupBy, uniqBy} from 'lodash-es';
import {DOCUMENT, isPlatformBrowser, ViewportScroller} from '@angular/common';
import {AppConfigService} from './app-config.service';
import {Platform} from '@angular/cdk/platform';
import {DeviceTypeEnum} from '../enums/DeviceTypeEnum';
import {QueryParams} from '../types/QueryParams';

export class CustomHttpParamEncoder implements HttpParameterCodec {
  encodeKey(key: string): string {
    return key;
  }

  encodeValue(value): string {
    return value != null ? (value !== 'null' ? value.toString().replace(/\+/gi, '%2B') : '') : value;
  }

  decodeKey(key: string): string {
    return key;
  }

  decodeValue(value: string): string {
    return value != null ? value.toString().replace(/%2B/gi, '+') : value;
  }
}

@Injectable({
  providedIn: 'root',
})
export class ToolsService {
  constructor(
    private appConfigService: AppConfigService,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: any,
    private viewportScroller: ViewportScroller,
    private platform: Platform,
    private ngZone: NgZone,
  ) {}

  public isNil(variable: any): boolean {
    return variable == null;
  }

  public isEqual(variable1: any, variable2: any): boolean {
    return JSON.stringify(variable1) === JSON.stringify(variable2);
  }

  public clone<T>(variable: T): T {
    if (typeof variable === 'undefined') {
      return undefined;
    }
    return !this.isNil(variable) ? JSON.parse(JSON.stringify(variable)) : null;
  }

  public recursiveDeepCopy(obj) {
    return Object.keys(obj).reduce(
      (v, d) =>
        Object.assign(v, {
          [d]: obj[d] && obj[d].constructor === Object ? this.recursiveDeepCopy(obj[d]) : obj[d],
        }),
      {},
    );
  }

  public buildHttpParams(data: any): HttpParams {
    let paramObj = {};

    this.fillHttpParamsObject(paramObj, data, '');

    let params = new HttpParams({
      fromObject: paramObj,
      encoder: new CustomHttpParamEncoder(),
    });

    return params;
  }

  public difference(array1: Array<any>, array2: Array<any>): Array<any> {
    const arrays = [array1, array2];
    return arrays.reduce((a, b) => a.filter((c) => !b.includes(c)));
  }

  public round(value: number, precision?: number): number {
    if (this.isNil(precision)) {
      precision = 0;
    }
    const power = Math.pow(10, precision);
    return Math.round(value * power) / power;
  }

  public debounce(func: Function, wait: number, immediate: boolean): Function {
    let timeout;
    return function () {
      const context = this;
      const args = arguments;
      clearTimeout(timeout);

      timeout = setTimeout(() => {
        timeout = null;
        if (!immediate) {
          func.apply(context, args);
        }
      }, wait);

      if (immediate && !timeout) {
        func.apply(context, args);
      }
    };
  }

  public getImageUrl(src: string): string {
    if (src.indexOf(this.appConfigService.appConfig.amazonS3Url) >= 0 || src.indexOf('data:image/jpg;base64') >= 0) {
      return src;
    } else if (src !== '') {
      return this.appConfigService.appConfig.amazonS3Url + src;
    } else {
      return '';
    }
  }

  public scrollTo(elementId?: string, delay?: number, correction?: number): void {
    if (this.isNil(delay)) {
      delay = 0;
    }
    if (this.isNil(correction)) {
      correction = 0;
    }
    if (isPlatformBrowser(this.platformId)) {
      this.ngZone.runOutsideAngular(() => {
        setTimeout(() => {
          this.ngZone.run(() => {
            const componentElement: HTMLElement = document.getElementById(elementId);
            let scrollToValue = 0;
            if (!this.isNil(componentElement)) {
              scrollToValue = this.getOffsetTop(componentElement) + correction;
            }
            this.viewportScroller.scrollToPosition([0, scrollToValue]);
          });
        }, delay);
      });
    }
  }

  public scrollToBounding(elementId?: string, delay?: number, correction?: number): void {
    if (this.isNil(delay)) {
      delay = 0;
    }
    if (this.isNil(correction)) {
      correction = 0;
    }
    if (isPlatformBrowser(this.platformId)) {
      this.ngZone.runOutsideAngular(() => {
        setTimeout(() => {
          this.ngZone.run(() => {
            const componentElement: HTMLElement = document.getElementById(elementId);
            let scrollToValue = 0;
            if (!this.isNil(componentElement)) {
              scrollToValue =
                componentElement.getBoundingClientRect().top +
                this.viewportScroller.getScrollPosition()[1] +
                correction;
            }
            this.viewportScroller.scrollToPosition([0, scrollToValue]);
          });
        }, delay);
      });
    }
  }

  public recaptchaFix(): void {
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        this.ngZone.run(() => {
          const recaptchaArrowElements: NodeListOf<Element> = document.querySelectorAll(
            '[title="recaptcha challenge"]',
          );
          if (recaptchaArrowElements.length > 0) {
            const recaptchaLastElement: Element = recaptchaArrowElements.item(recaptchaArrowElements.length - 1);
            this.updateRecaptchaPosition(this.getParentElement(this.getParentElement(recaptchaLastElement)));
          }
        });
      }, 2000);
    });
  }

  public getRandomInt(min, max): number {
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min)) + min;
  }

  public isInArray(array: any[], element: any): boolean {
    const findedEl = array.find((item) => element.indexOf(item) >= 0);
    return findedEl !== undefined && findedEl !== null;
  }

  public chunk<T>(array: T[], parts: number) {
    const inputArray = this.clone(array);
    const result = [];
    for (let i = parts; i > 0; i--) {
      result.push(inputArray.splice(0, Math.ceil(inputArray.length / i)));
    }
    return result;
  }

  public sortBy = (key) => {
    return (a, b) => (a[key] > b[key] ? 1 : b[key] > a[key] ? -1 : 0);
  };

  public isPrerender(): boolean {
    return this.getDeviceType() === DeviceTypeEnum.HEADLESS;
  }

  public areStringsEqualIgnoringEncoding(str1: string, str2: string): boolean {
    return str1 === str2 || encodeURI(str1) === str2 || str1 === encodeURI(str2);
  }

  public groupBy<T>(array: Array<T>, key: string): any {
    return groupBy(array, key);
  }

  public uniqueBy<T>(array: Array<T>, key: string): any {
    return uniqBy(array, key);
  }

  public equals(a, b): boolean {
    if (this.isNil(a) && this.isNil(b)) {
      return true;
    }
    if (this.isNil(a) || this.isNil(b)) {
      return false;
    }
    return a.length === b.length && a.every((v, i) => v === b[i]);
  }

  public isLocationInSwedenArea(lat: number, lon: number): boolean {
    const n: number = 69.06;
    const s: number = 55.33;
    const w: number = 11.11;
    const e: number = 24.15;

    return lat <= n && lat >= s && lon >= w && lon <= e;
  }

  public getDeviceType(): DeviceTypeEnum {
    if (this.platform.ANDROID || this.platform.IOS) {
      return DeviceTypeEnum.MOBILE;
    } else if (this.platform.isBrowser) {
      return DeviceTypeEnum.DESKTOP;
    } else {
      return DeviceTypeEnum.HEADLESS;
    }
  }

  public shortenHtmlDescription(htmlDescription: string, maxLength: number): string {
    if (htmlDescription === null || htmlDescription === '') {
      return '';
    }
    const plainText = htmlDescription.replace(/<[^>]+>/g, ' ');
    let trimmedText = plainText.substring(0, maxLength);
    if (plainText.length > maxLength) {
      const lastSpace = trimmedText.lastIndexOf(' ');
      trimmedText = trimmedText.substring(0, lastSpace);
      trimmedText += '...';
    }
    return trimmedText;
  }

  public removeHtmlTags(str: string): string {
    return str ? str.replace(/<[^>]*>/g, ' ') : str;
  }

  public areQueryParamsEqual(queryParams1: QueryParams, queryParams2: QueryParams): boolean {
    const keys1 = Object.keys(queryParams1);
    const keys2 = Object.keys(queryParams2);

    if (keys1.length !== keys2.length) {
      return false;
    }

    for (let i = 0; i < keys1.length; i++) {
      if (keys1[i] !== keys2[i]) {
        return false;
      }
    }

    for (let i = 0; i < keys1.length; i++) {
      const key = keys1[i];
      const value1 = queryParams1[key];
      const value2 = queryParams2[key];

      if (String(value1) !== String(value2)) {
        return false;
      }
    }

    return true;
  }

  private getOffsetTop(element): number {
    let offsetTop = 0;
    while (element) {
      offsetTop += element.offsetTop;
      element = element.offsetParent;
    }
    return offsetTop;
  }

  private fillHttpParamsObject(params: any, data: any, currentPath: string): void {
    Object.keys(data).forEach((key) => {
      if (data[key] instanceof Array) {
        data[key].forEach((item, index) => {
          let arrayItem = {};
          this.fillArrayItemData(arrayItem, item, `[${index}]`);

          Object.keys(arrayItem).forEach((arrayItemKey) => {
            params[`${currentPath}${key}${arrayItemKey}`] = arrayItem[arrayItemKey];
          });
        });
      } else if (data[key] instanceof Object) {
        this.fillHttpParamsObject(params, data[key], `${currentPath}${key}.`);
      } else {
        params[`${currentPath}${key}`] = data[key];
      }
    });
  }

  private fillArrayItemData(params: any, data: any, currentPath: string): void {
    if (data instanceof Object) {
      Object.keys(data).forEach((key) => {
        if (data[key] instanceof Object) {
          this.fillArrayItemData(params, data[key], `${currentPath}.${key}`);
        } else {
          params[`${currentPath}.${key}`] = data[key];
        }
      });
    } else {
      params[`${currentPath}`] = data;
    }
  }

  private getParentElement(el: Element): Element {
    return el.parentElement;
  }

  private updateRecaptchaPosition(el: Element): void {
    if (!this.isNil(el)) {
      let cssClasses: string = 'fixed-element absolute-top';
      if (this.getDeviceType() === DeviceTypeEnum.MOBILE) {
        cssClasses += ' fixed-zero';
      }
      el.setAttribute('class', cssClasses);
    }
  }
}
